import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import clsx from 'clsx'
import TDSDialog from '~shared/lib/TDSDialog/TDSDialog'
import KdsAuthorizationStore from '../../Stores/KdsAuthorizationStore'
import { KdsAuthorizationError, KdsAuthorizationErrorReason } from '../../Types/Authorization'
import styles from './KdsPinModal.module.scss'

const PIN_LENGTH = 6
function cooldownMessage(remainingSeconds: number): string {
  return `Too many incorrect PIN attempts. Try again in ${remainingSeconds} ${
    remainingSeconds === 1 ? 'second' : 'seconds'
  }.`
}

// Typed reasons map to safe, generic copy. Never surface server-localized text or token details.
function errorMessage(error: KdsAuthorizationError): string | null {
  switch (error.reason) {
    case KdsAuthorizationErrorReason.InvalidPin:
      return 'Incorrect PIN. Please try again.'
    case KdsAuthorizationErrorReason.IneligibleOperator:
      return 'You are not authorized for this action.'
    case KdsAuthorizationErrorReason.Cooldown:
      // Superseded by the static cooldown message while the cooldown is active; nothing to show once lapsed.
      return null
    case KdsAuthorizationErrorReason.TransportError:
      return 'Something went wrong. Please try again.'
    case KdsAuthorizationErrorReason.Cancelled:
      // The prompt is closing; no message needed.
      return null
  }
}

const KdsPinModalComponent = () => {
  const { submitting, error, cooldownUntil } = KdsAuthorizationStore
  const [pin, setPin] = useState('')
  const coolingDown = cooldownUntil > Date.now()
  const inputDisabled = submitting || coolingDown

  useEffect(() => {
    if (error) {
      setPin('')
    }
  }, [error])

  useEffect(() => {
    if (pin.length === PIN_LENGTH && !coolingDown) {
      KdsAuthorizationStore.submitPin(pin)
    }
  }, [pin, coolingDown])

  // Clear the cooldown once it lapses while the modal is open, re-enabling the keypad.
  useEffect(() => {
    const remainingMs = cooldownUntil - Date.now()
    if (remainingMs <= 0) {
      return
    }

    const timeout = window.setTimeout(() => KdsAuthorizationStore.expireCooldown(), remainingMs)
    return () => window.clearTimeout(timeout)
  }, [cooldownUntil])

  const appendDigit = (digit: string) => {
    if (inputDisabled) {
      return
    }
    setPin(current => (current.length >= PIN_LENGTH ? current : current + digit))
  }

  const removeDigit = () => {
    if (inputDisabled) {
      return
    }
    setPin(current => current.slice(0, -1))
  }

  const cancel = () => {
    KdsAuthorizationStore.cancelPending('cancelled')
  }

  const remainingSeconds = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000))
  const message = coolingDown
    ? cooldownMessage(remainingSeconds)
    : error
      ? errorMessage(error)
      : null

  return (
    <TDSDialog
      toggleDialog={cancel}
      header="Enter your PIN"
      showCloseIcon={false}
      customClass={styles.dialog}
    >
      <div
        className={styles.body}
        role="dialog"
        aria-modal="true"
        aria-label="Enter your PIN"
        data-testid="kds-pin-modal"
      >
        <div className={styles.dots} aria-hidden="true" data-testid="kds-pin-display">
          {Array.from({ length: PIN_LENGTH }).map((_, index) => (
            <span
              key={index}
              className={clsx(styles.dot, index < pin.length && styles.dotFilled)}
            />
          ))}
        </div>
        <span className={styles.srOnly} aria-live="polite" data-testid="kds-pin-progress">
          {`${pin.length} of ${PIN_LENGTH} digits entered`}
        </span>

        <div className={styles.messageArea}>
          {submitting ? (
            <div className={styles.status} role="status" data-testid="kds-pin-status">
              Verifying...
            </div>
          ) : (
            message && (
              <div className={styles.error} role="alert" data-testid="kds-pin-error">
                {message}
              </div>
            )
          )}
        </div>

        <div className={styles.keypad}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(digit => (
            <button
              key={digit}
              type="button"
              className={styles.key}
              onClick={() => appendDigit(digit)}
              disabled={inputDisabled}
              data-testid={`kds-pin-key-${digit}`}
            >
              {digit}
            </button>
          ))}
          <span className={styles.keySpacer} aria-hidden="true" />
          <button
            type="button"
            className={styles.key}
            onClick={() => appendDigit('0')}
            disabled={inputDisabled}
            data-testid="kds-pin-key-0"
          >
            0
          </button>
          <button
            type="button"
            className={clsx(styles.key, styles.keyUtility)}
            onClick={removeDigit}
            disabled={inputDisabled || pin.length === 0}
            aria-label="Delete last digit"
            data-testid="kds-pin-backspace"
          >
            ⌫
          </button>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={clsx(styles.actionButton, styles.secondary)}
            onClick={cancel}
            data-testid="kds-pin-cancel"
          >
            Cancel
          </button>
        </div>

        <p className={styles.forgot} data-testid="kds-pin-forgot">
          Forgot your PIN? See your store manager.
        </p>
      </div>
    </TDSDialog>
  )
}

export const KdsPinModal = observer(KdsPinModalComponent)
